import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hover intent for one block's info card.
 *
 * Two delays, not one, and they do different jobs:
 *
 * - **Opening is delayed** so that dragging the pointer across a packed row does not flash a card
 *   over every block on the way. A block is 60px tall and a busy Handwerker's row holds a dozen of
 *   them; without this the board strobes.
 * - **Closing is delayed** so the pointer can cross the gap between the block and the card without
 *   dismissing it. That gap is what makes the `tel:` link clickable and the Schadensbeschreibung
 *   selectable — a card that vanished on `pointerleave` would be a tooltip, not a card.
 *
 * The two together also guarantee only one card is ever up: leaving A schedules its close at +150ms
 * and entering B opens at +400ms, so B's card cannot appear while A's is still standing.
 *
 * The timings are the deleted `timeline-day/ui/AppointmentBlock.tsx`'s, kept deliberately — this is
 * the popover that widget had, rebuilt for the board.
 */
const HOVER_OPEN_MS = 400;
const HOVER_CLOSE_MS = 150;

export interface BlockHoverCard {
  /**
   * The hovered block's box in viewport coordinates, or `null` when no card is up. Captured once at
   * open time: the board scrolls in two axes, and re-measuring on scroll would make the card chase
   * the block out from under the pointer.
   */
  anchorRect: DOMRect | null;
  /** Pointer entered the block — open after the delay, measuring `el` when the timer fires. */
  open: (el: HTMLElement) => void;
  /** Pointer left the block or the card — close after the grace period. */
  scheduleClose: () => void;
  /** Pointer entered the card — it is still wanted, so call off the pending close. */
  cancelClose: () => void;
  /** A drag or a click started, or the block went away. Drop the card now, cancel any pending open. */
  closeNow: () => void;
}

export function useBlockHoverCard(): BlockHoverCard {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const clearOpen = useCallback(() => {
    if (openTimer.current == null) return;
    window.clearTimeout(openTimer.current);
    openTimer.current = null;
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current == null) return;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  // A block unmounts on every schedule refetch and on every day change; a timer left running would
  // set state on a dead component, and worse, open a card for a block that is no longer there.
  useEffect(
    () => () => {
      clearOpen();
      cancelClose();
    },
    [clearOpen, cancelClose],
  );

  const open = useCallback(
    (el: HTMLElement) => {
      cancelClose();
      clearOpen();
      // Measured inside the timeout, not now: 400ms is long enough for the row to re-pack or the
      // board to scroll, and a rect taken on entry would place the card where the block used to be.
      openTimer.current = window.setTimeout(() => {
        openTimer.current = null;
        setAnchorRect(el.getBoundingClientRect());
      }, HOVER_OPEN_MS);
    },
    [cancelClose, clearOpen],
  );

  const scheduleClose = useCallback(() => {
    // Leaving before the card ever opened cancels the open outright — there is nothing to grace.
    clearOpen();
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setAnchorRect(null);
    }, HOVER_CLOSE_MS);
  }, [clearOpen, cancelClose]);

  const closeNow = useCallback(() => {
    clearOpen();
    cancelClose();
    setAnchorRect(null);
  }, [clearOpen, cancelClose]);

  return { anchorRect, open, scheduleClose, cancelClose, closeNow };
}
